'use client'

import { useEffect, useState } from 'react'
import type { AskShellMode } from './AskShell'

/**
 * Keeps the Ask shell from competing with a form the visitor is filling in
 * (PIX-66). The launcher pill sits bottom-right on every page, which on a
 * phone with the keyboard open is exactly where a form's fields and submit
 * button end up (the 2026-09-20 heuristic evaluation, F8, named the free
 * check, the contact form and the footer newsletter).
 *
 * Marks the shell's own surfaces: a field inside one of them is the Ask bar,
 * which must never hide itself.
 */
export const ASK_SHELL_ATTR = 'data-ask-shell'

/** True for a field inside a <form> that is not part of the Ask shell. */
export function isExternalFormField(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  if (!el.matches('input, textarea, select')) return false
  if (!el.closest('form')) return false
  return !el.closest(`[${ASK_SHELL_ATTR}]`)
}

/**
 * Whether the shell steps aside: only while an external form field has focus
 * AND the shell is passive. A visitor with suggestions or an answer open is
 * using the chat, so it stays.
 */
export function shouldYieldToForm(
  mode: AskShellMode,
  formFieldFocused: boolean
): boolean {
  if (!formFieldFocused) return false
  return mode === 'minimized' || mode === 'initial' || mode === 'docked'
}

/**
 * True while focus is on an external form field. Reads the event targets
 * rather than document.activeElement, which is not yet updated while
 * focusout fires: focusin reports the field gaining focus, focusout the one
 * gaining it next (relatedTarget, null when focus leaves the page's fields).
 */
export function useFormFieldFocus(): boolean {
  const [focused, setFocused] = useState(false)
  useEffect(() => {
    const onFocusIn = (e: FocusEvent) =>
      setFocused(isExternalFormField(e.target))
    const onFocusOut = (e: FocusEvent) =>
      setFocused(isExternalFormField(e.relatedTarget))
    setFocused(isExternalFormField(document.activeElement))
    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('focusout', onFocusOut)
    return () => {
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('focusout', onFocusOut)
    }
  }, [])
  return focused
}
